import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Lock, MinusCircle, PlusCircle, RefreshCw, AlertTriangle, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
};

function ChangeList({ title, items, tone, testId }) {
  const Icon = tone === 'gain' ? PlusCircle : MinusCircle;
  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 p-4" data-testid={testId}>
      <div className={cn('ae-eyebrow mb-3', tone === 'gain' ? 'text-ae-success' : 'text-amber-600')}>{title}</div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{tone === 'gain' ? 'No new features.' : 'You keep everything you have today.'}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.label} className="flex items-start gap-2 text-xs">
              <Icon className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', tone === 'gain' ? 'text-ae-success' : 'text-amber-600')} />
              <span className="min-w-0">
                <span className="font-semibold text-ae-ink dark:text-white">{it.label}</span>
                <span className="block text-muted-foreground">{it.from} → <strong className="text-ae-ink dark:text-slate-200">{it.to}</strong></span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SELLER_STATE = '06';

export const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const isValidGstin = (g, states) => GSTIN_RE.test(g || '') && (!states.length || states.some((st) => st.code === g.slice(0, 2)));

function StatePicker({ states, value, onChange, business, setBusiness }) {
  const gstinValid = isValidGstin(business.gstin, states);
  const setB = (patch) => {
    const next = { ...business, ...patch };
    setBusiness(next);
    if (next.enabled && isValidGstin(next.gstin, states)) onChange(next.gstin.slice(0, 2));
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 p-4 space-y-3" data-testid="buyer-state-picker">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Your state</div>
          <div className="text-[11px] text-muted-foreground">Needed for your GST tax invoice (place of supply).</div>
        </div>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={business.enabled && gstinValid}
          data-testid="buyer-state-select"
          className="h-10 sm:w-64 px-3 text-sm rounded-xl border border-input bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-ae-blue/30 focus:border-ae-blue disabled:opacity-70"
        >
          <option value="">Select state / UT…</option>
          {states.map((st) => <option key={st.code} value={st.code}>{st.name}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none" data-testid="business-invoice-toggle">
        <input type="checkbox" checked={business.enabled} onChange={(e) => setB({ enabled: e.target.checked })} className="h-4 w-4 accent-[#4568F5]" />
        Business purchase — add my GSTIN to claim input tax credit
      </label>
      {business.enabled && (
        <div className="grid sm:grid-cols-2 gap-2.5">
          <div>
            <input
              value={business.gstin}
              onChange={(e) => setB({ gstin: e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 15) })}
              placeholder="GSTIN (15 characters)"
              data-testid="buyer-gstin-input"
              className="h-10 w-full px-3 text-sm font-mono rounded-xl border border-input bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-ae-blue/30 focus:border-ae-blue"
            />
            {business.gstin && !gstinValid && (
              <div className="mt-1 text-[11px] text-rose-600" data-testid="buyer-gstin-error">Enter a valid 15-character GSTIN.</div>
            )}
          </div>
          <input
            value={business.name}
            onChange={(e) => setB({ name: e.target.value.slice(0, 120) })}
            placeholder="Registered business name"
            data-testid="buyer-business-name-input"
            className="h-10 w-full px-3 text-sm rounded-xl border border-input bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-ae-blue/30 focus:border-ae-blue"
          />
        </div>
      )}
    </div>
  );
}

export function PlanChangePreview({
  preview, busy, onBack, onConfirm, states = [], buyerState = '', setBuyerState = () => {},
  business = { enabled: false, gstin: '', name: '' }, setBusiness = () => {},
}) {
  const businessOk = !business.enabled || (isValidGstin(business.gstin, states) && business.name.trim().length > 1);
  const down = preview.direction === 'downgrade';
  const same = preview.direction === 'same';
  if (same) {
    return (
      <div className="py-6 text-center space-y-3" data-testid="plan-change-preview">
        <CheckCircle2 className="h-9 w-9 text-ae-success mx-auto" />
        <div className="text-sm font-semibold" data-testid="preview-same-plan">You're already on the {preview.toName} plan.</div>
        <div className="text-xs text-muted-foreground">Active until {fmtDate(preview.currentExpiresAt)} — nothing to pay.</div>
        <Button variant="outline" onClick={onBack} data-testid="plan-preview-back" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> All plans</Button>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="plan-change-preview">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-bold uppercase" data-testid="preview-from-tier">{preview.fromTier}</span>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <span className="px-3 py-1 rounded-full bg-ae-blue text-white text-xs font-bold uppercase" data-testid="preview-to-tier">{preview.toTier}</span>
        <span className={cn('ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-md', down ? 'bg-amber-100 text-amber-800' : 'bg-ae-success/10 text-ae-success')} data-testid="preview-direction">
          {down ? 'Downgrade' : preview.direction === 'renew' ? 'Renewal' : 'Upgrade'}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <ChangeList title="You gain" items={preview.gains} tone="gain" testId="preview-gains" />
        <ChangeList title="You lose" items={preview.losses} tone="loss" testId="preview-losses" />
      </div>
      <div className="rounded-2xl bg-ae-bg dark:bg-slate-800/50 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="preview-billing">
        <div><div className="ae-eyebrow">Pay today</div><div className="mt-1 text-lg font-heading font-extrabold text-ae-blue" data-testid="preview-amount">{inr(preview.amount)}</div></div>
        <div>
          <div className="ae-eyebrow">{!buyerState ? 'Incl. GST 18%' : buyerState === SELLER_STATE ? 'Incl. CGST+SGST' : 'Incl. IGST 18%'}</div>
          <div className="mt-1 font-semibold" data-testid="preview-gst-amount">{inr(preview.gst?.gstAmount)}</div>
        </div>
        <div><div className="ae-eyebrow">Starts</div><div className="mt-1 font-semibold" data-testid="preview-starts-on">{preview.scheduled ? fmtDate(preview.startsOn) : 'Immediately'}</div></div>
        <div><div className="ae-eyebrow">Renews on</div><div className="mt-1 font-semibold" data-testid="preview-renews-on">{fmtDate(preview.renewsOn)}</div></div>
      </div>
      {preview.currentExpiresAt && !down && preview.direction !== 'renew' && (
        <p className="text-[11px] text-muted-foreground">
          Your remaining days are carried over — the new 30-day period is added after {fmtDate(preview.currentExpiresAt)}.
        </p>
      )}
      {preview.direction === 'renew' && (
        <p className="text-[11px] text-ae-success" data-testid="preview-renew-note">
          Renewing early is safe — 30 days are added after your current plan ends on {fmtDate(preview.currentExpiresAt)}.
        </p>
      )}
      {preview.scheduled && (
        <p className="text-[11px] text-amber-700 dark:text-amber-300" data-testid="preview-downgrade-warning">
          You keep your prepaid {preview.fromTier.toUpperCase()} plan until {fmtDate(preview.currentExpiresAt)}. Your {preview.toName} plan starts automatically on that date — nothing is lost.
        </p>
      )}
      <StatePicker states={states} value={buyerState} onChange={setBuyerState} business={business} setBusiness={setBusiness} />
      <div className="flex items-center justify-between gap-3 pt-1">
        <Button variant="ghost" onClick={onBack} disabled={busy} data-testid="plan-preview-back" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> All plans</Button>
        <Button onClick={onConfirm} disabled={busy || !buyerState || !businessOk} title={!buyerState ? 'Select your state first' : !businessOk ? 'Complete your GSTIN and business name' : ''} size="lg" data-testid="plan-preview-confirm" className="gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {busy ? 'Opening secure checkout…' : `Confirm & pay ${inr(preview.amount)}`}
        </Button>
      </div>
    </div>
  );
}

export function CheckoutResult({ result, onRetry, onClose }) {
  if (result.status === 'verifying') {
    return (
      <div className="py-10 text-center" data-testid="checkout-verifying">
        <Loader2 className="h-8 w-8 animate-spin text-ae-blue mx-auto" />
        <div className="mt-3 text-sm font-semibold">Confirming your payment…</div>
        <div className="text-xs text-muted-foreground">Please don't close this window.</div>
      </div>
    );
  }
  if (result.status === 'error') {
    return (
      <div className="py-6 text-center space-y-3" data-testid="checkout-error">
        <AlertTriangle className="h-9 w-9 text-amber-500 mx-auto" />
        <div className="text-sm font-semibold">We couldn't confirm this payment yet.</div>
        <div className="text-xs text-muted-foreground">{result.message}</div>
        <div className="text-[11px] text-muted-foreground max-w-sm mx-auto">
          If money was debited, it's safe — retry below, or email <a className="text-ae-blue underline" href="mailto:info@actanonedu.com">info@actanonedu.com</a> with the Payment ID and we'll activate your plan.
        </div>
        {result.paymentId && <div className="text-[11px] text-muted-foreground">Payment ID: <span className="font-mono">{result.paymentId}</span></div>}
        <Button onClick={onRetry} className="gap-2" data-testid="checkout-retry-verify"><RefreshCw className="h-4 w-4" /> Retry activation</Button>
      </div>
    );
  }
  const sub = result.data?.subscription || {};
  const pay = result.data?.payment || {};
  return (
    <div className="py-4 text-center space-y-4" data-testid="checkout-success">
      <div className="h-14 w-14 rounded-full bg-ae-success/10 flex items-center justify-center mx-auto">
        <CheckCircle2 className="h-8 w-8 text-ae-success" />
      </div>
      <div>
        <div className="text-lg font-heading font-extrabold" data-testid="checkout-success-title">
          {(pay.planName || sub.tier || '').toString()} plan {result.data?.scheduled ? 'scheduled' : 'activated'}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {result.data?.scheduled
            ? `Your current plan stays active until ${fmtDate(result.data.startsOn)}; ${pay.planName} starts then.`
            : 'All features are unlocked right now.'}
        </div>
      </div>
      <div className="rounded-2xl bg-ae-bg dark:bg-slate-800/50 p-4 grid grid-cols-3 gap-3 text-xs text-left">
        <div><div className="ae-eyebrow">Paid</div><div className="mt-1 font-semibold">{pay.amount != null ? inr(pay.amount) : '—'}</div></div>
        <div><div className="ae-eyebrow">Renews on</div><div className="mt-1 font-semibold">{fmtDate(result.data?.renewsOn || sub.expiresAt)}</div></div>
        <div className="min-w-0"><div className="ae-eyebrow">Payment ID</div><div className="mt-1 font-mono truncate">{pay.razorpayPaymentId || '—'}</div></div>
      </div>
      <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> A GST tax invoice is on its way to your email — also in My Profile → Billing & receipts.</div>
      <div><Button onClick={onClose} data-testid="checkout-success-close">Start creating</Button></div>
    </div>
  );
}
